import {computed, ref, Ref} from 'vue'
import {UserSettingName} from '@toolify/server/src/models/mongoose/UserModel/enum/UserSettingName'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {
  LightModernPaletteVariantType,
} from '@toolify/server/src/services/UserSettingsService/strategies/LightModernPaletteVariantUserSettingStrategy/enum/LightModernPaletteVariantType'
import {
  LightModernBadgeVariantType,
} from '@toolify/server/src/services/UserSettingsService/strategies/LightModernBadgeVariantUserSettingStrategy/enum/LightModernBadgeVariantType'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {useMeApiStore} from '@toolify/client/src/stores/api/MeApiStore/useMeApiStore'
import {readThemeBootState} from '@toolify/client/src/modules/theme/functions/themeBootState'

// wybor z chwili zapisu: kolory zmieniaja sie od razu, a nie dopiero po ponownym pobraniu konta.
// Wspolny dla calej aplikacji, bo palete i plakietki czyta wiele composables naraz
const pendingPaletteVariant = ref<LightModernPaletteVariantType | null>(null)
const pendingBadgeVariant = ref<LightModernBadgeVariantType | null>(null)
const latestSaveIds: Partial<Record<UserSettingName, number>> = {}

function isPaletteVariant(value: unknown): value is LightModernPaletteVariantType {
  return Object.values(LightModernPaletteVariantType).includes(value as LightModernPaletteVariantType)
}

function isBadgeVariant(value: unknown): value is LightModernBadgeVariantType {
  return Object.values(LightModernBadgeVariantType).includes(value as LightModernBadgeVariantType)
}

// Paleta i set kolorow plakietek motywu light - ustawienia konta (Ustawienia -> Wyglad). Przed pobraniem uzytkownika
// paleta z poprzedniego uruchomienia (themeBootState), jak sam motyw: bez tego start w Grafice mrugal Lawenda
export function useLightModernAppearance() {
  const authStore = useAuthStore()
  const meApiStore = useMeApiStore()

  const currentUser = computed<IUserResponseInstance | null>(() => {
    return authStore.currentEntity?.entityType === EntityType.User
      ? authStore.currentEntity.payload as IUserResponseInstance
      : null
  })

  const paletteVariant = computed<LightModernPaletteVariantType>(() => {
    if (pendingPaletteVariant.value) {
      return pendingPaletteVariant.value
    }
    const savedValue = currentUser.value?.settings?.[UserSettingName.LightModernPaletteVariant]
    if (isPaletteVariant(savedValue)) {
      return savedValue
    }
    const rememberedValue = authStore.currentEntity ? null : readThemeBootState()?.attributes['data-lm-palette']
    return isPaletteVariant(rememberedValue) ? rememberedValue : LightModernPaletteVariantType.Lavender
  })

  const badgeVariant = computed<LightModernBadgeVariantType>(() => {
    if (pendingBadgeVariant.value) {
      return pendingBadgeVariant.value
    }
    const savedValue = currentUser.value?.settings?.[UserSettingName.LightModernBadgeVariant]
    return isBadgeVariant(savedValue) ? savedValue : LightModernBadgeVariantType.AccentText
  })

  async function saveSetting<T>(settingName: UserSettingName, value: T, pendingValue: Ref<T | null>): Promise<void> {
    const saveId = (latestSaveIds[settingName] ?? 0) + 1
    latestSaveIds[settingName] = saveId
    pendingValue.value = value
    try {
      await meApiStore.updateSetting(settingName, value)
    } catch (error) {
      // zapis sie nie udal - wraca wartosc z konta; blad pokazuje juz samo zapytanie
      if (latestSaveIds[settingName] === saveId) {
        pendingValue.value = null
      }
      return
    }
    try {
      await authStore.refetchCurrentEntity()
    } catch (error) {
      // zapisane, ale konta nie udalo sie pobrac ponownie - do tego czasu konto pokazuje zapisana wartosc
      const user = currentUser.value
      if (user) {
        user.settings = {
          ...user.settings,
          [settingName]: value,
        }
      }
    } finally {
      // starszy zapis nie kasuje nowszego wyboru, ktory jeszcze sie zapisuje
      if (latestSaveIds[settingName] === saveId) {
        pendingValue.value = null
      }
    }
  }

  async function setPaletteVariant(value: LightModernPaletteVariantType): Promise<void> {
    if (!currentUser.value || value === paletteVariant.value) {
      return
    }
    await saveSetting(UserSettingName.LightModernPaletteVariant, value, pendingPaletteVariant)
  }

  async function setBadgeVariant(value: LightModernBadgeVariantType): Promise<void> {
    if (!currentUser.value || value === badgeVariant.value) {
      return
    }
    await saveSetting(UserSettingName.LightModernBadgeVariant, value, pendingBadgeVariant)
  }

  return {
    paletteVariant,
    badgeVariant,
    setPaletteVariant,
    setBadgeVariant,
  }
}
